/**
 * Everything the settings page reads and writes, in one module.
 *
 * The RMS keeps a file per endpoint group because it has forty of them;
 * intake has five sections and one screen, and splitting a handful of
 * small contracts across as many files would make the shape of the API
 * harder to see rather than easier. Each block below mirrors one handler
 * group in go-service/internal/api.
 *
 * Every endpoint answers with its payload under one key — `{ settings }`,
 * `{ phoneNumbers }` — and a Go nil slice arrives as `null`, so the key
 * and what an absent payload means are the whole of what a function
 * here knows about its wire shape, and `envelope` is where that is
 * read. The functions that do more than unwrap (the users list, the
 * receptionist knowledge, the questions) say so in their own bodies.
 * Tested against a fake `fetch` (tests/lib/settings-api.test.ts), not
 * by mocking this module: the screen tests mock these by name, so the
 * wire shape is checked nowhere else.
 */
import { apiFetch } from "../../../shared/api-client";

type RequestOptions = NonNullable<Parameters<typeof apiFetch>[1]>;

/**
 * One request, one envelope: the payload under `key`, or `absent` where
 * the server sent `null` or nothing for it.
 */
async function envelope<T>(
  path: string,
  key: string,
  opts: RequestOptions = {},
  absent?: T,
): Promise<T> {
  const res = await apiFetch<Record<string, T | null | undefined>>(
    path,
    opts,
  );
  return res[key] ?? (absent as T);
}

// ─── Company settings (company_settings.go) ───────────────────────────

export interface CompanySettings {
  name: string;
  /** Where this Company's own New Inquiry emails land. */
  contact_email: string;
  /** What the Receptionist may say about the Company on a call. */
  receptionist_facts: string;
  logo_url: string;
  /**
   * What a drafted job is called in the rental system, as a template of
   * `{placeholders}`. Empty is the rental system's own name.
   */
  job_name_format: string;
}

/** Partial update — only supplied keys are written. */
export type CompanySettingsUpdate = Partial<CompanySettings>;

export const getCompanySettings = (
  signal?: AbortSignal,
): Promise<CompanySettings> =>
  envelope("/api/admin/company-settings", "settings", { signal });

export const saveCompanySettings = (
  payload: CompanySettingsUpdate,
): Promise<CompanySettings> =>
  envelope("/api/admin/company-settings", "settings", {
    method: "PUT",
    body: payload,
  });

// ─── Logo (company_logo.go) ───────────────────────────────────────────

export function uploadCompanyLogo(file: File): Promise<string> {
  const body = new FormData();
  body.append("logo", file);
  return envelope("/api/admin/company-logo", "logo_url", {
    method: "PUT",
    body,
  });
}

export async function removeCompanyLogo(): Promise<void> {
  await apiFetch<unknown>("/api/admin/company-logo", { method: "DELETE" });
}

// ─── Phone numbers (phone_numbers.go) ─────────────────────────────────

export interface PhoneNumber {
  id: string;
  /** Provisioning and decommissioning are out of band; neither is edited. */
  e164: string;
  forward_to: string;
  profile_key: string;
  greeting: string;
  voicemail_prompt: string;
  /**
   * Where the Company recorded the greeting or the voicemail prompt
   * rather than have the words said (migration 076), the URL it plays
   * from; empty where the words are said. Set and cleared by
   * `uploadPromptRecording` and `removePromptRecording`, never by a save.
   */
  greeting_recording_url: string;
  voicemail_recording_url: string;
  recording_enabled: boolean;
  /**
   * How long the number rings before a missed call goes to the
   * Receptionist or voicemail: whole seconds, 5 to 60.
   */
  ring_secs: number;
  receptionist_enabled: boolean;
  /**
   * Off, the Receptionist picks up at every hour. On, only from
   * `receptionist_from` to `receptionist_until` on `receptionist_days`,
   * and all day on public holidays.
   */
  receptionist_set_times: boolean;
  /**
   * HH:MM in New Zealand time, or both blank for all day on the days it
   * picks up on. The window may wrap midnight. The API keeps the pair
   * whole: a save that sets one sets the other.
   */
  receptionist_from: string;
  receptionist_until: string;
  /** ISO weekdays it picks up on, Monday 1 to Sunday 7. */
  receptionist_days: number[];
  receptionist_name: string;
}

export type PhoneNumberUpdate = { id: string } & Partial<
  Omit<
    PhoneNumber,
    "id" | "e164" | "greeting_recording_url" | "voicemail_recording_url"
  >
>;

/** The two prompts a number can play a recording for. */
export type RecordedPrompt = "greeting" | "voicemail";

export const listPhoneNumbers = (
  signal?: AbortSignal,
): Promise<PhoneNumber[]> =>
  envelope("/api/admin/phone-numbers", "phoneNumbers", { signal }, []);

export const savePhoneNumber = (
  patch: PhoneNumberUpdate,
): Promise<PhoneNumber> =>
  envelope("/api/admin/phone-numbers", "phoneNumber", {
    method: "PATCH",
    body: patch,
  });

/**
 * Play a recording for one prompt in place of the words: an 8 kHz mono
 * WAV, as `toTelephoneWav` makes one (ADR-0035). The number as it now
 * stands comes back.
 */
export function uploadPromptRecording(
  numberId: string,
  prompt: RecordedPrompt,
  wav: Blob,
): Promise<PhoneNumber> {
  const body = new FormData();
  body.append("audio", wav, `${prompt}.wav`);
  return envelope(
    `/api/admin/phone-numbers/${encodeURIComponent(numberId)}/recordings/${prompt}`,
    "phoneNumber",
    { method: "PUT", body },
  );
}

/** Say the words again. The number as it now stands comes back. */
export const removePromptRecording = (
  numberId: string,
  prompt: RecordedPrompt,
): Promise<PhoneNumber> =>
  envelope(
    `/api/admin/phone-numbers/${encodeURIComponent(numberId)}/recordings/${prompt}`,
    "phoneNumber",
    { method: "DELETE" },
  );

// ─── Rental system (rental_system.go, intake_admin_api.go) ────────────

export type RentalSystemKind = "hireos" | "current_rms" | "rentman" | "";

export interface RentalSystem {
  kind: RentalSystemKind;
  /** "" (none) | disconnected | active | broken. */
  status: string;
  /** The workspace host, or the Current RMS account — never a key. */
  address?: string;
  last_error?: string;
  store_id?: number;
  owned_by?: number;
}

export interface CurrentRMSStore {
  id: number;
  name: string;
  address: string;
}

export async function getRentalSystem(
  signal?: AbortSignal,
): Promise<{ rental_system: RentalSystem; slug: string }> {
  return apiFetch("/api/admin/rental-system", { signal });
}

/**
 * Save a connection: which rental system, where it is, and a key that
 * system issued.
 *
 * One call for every kind, because the decision is the same one — this
 * Company's jobs draft there, with this credential — and only what names
 * the account differs: a workspace API host, a Current RMS subdomain and
 * the store its opportunities come out of, or a Rentman account.
 */
export async function connectRentalSystem(body: {
  kind: RentalSystemKind;
  base_url?: string;
  api_key: string;
  subdomain?: string;
  store_id?: number;
}): Promise<RentalSystem> {
  return envelope("/api/admin/rental-system", "rental_system", {
    method: "PUT",
    body,
  });
}

export const disconnectRentalSystem = (): Promise<RentalSystem> =>
  envelope("/api/admin/rental-system", "rental_system", {
    method: "DELETE",
  });

/**
 * Prove a Current RMS key and list the warehouses it can see.
 *
 * The form asks this before it connects, because an account with
 * several warehouses has to name the one its opportunities draft out of
 * and the credential itself is what the question needs to be asked
 * with. Nothing is saved by asking, so a Company testing a new key
 * cannot break the connection it is still running on.
 */
export async function listCurrentRMSStores(body: {
  subdomain: string;
  api_key: string;
}): Promise<CurrentRMSStore[]> {
  return envelope(
    "/api/admin/rental-system/current-rms/stores",
    "stores",
    { method: "POST", body },
    [],
  );
}

// ─── Calendar (calendar_admin_api.go) ────────────────────────────────

export type CalendarProvider = "google" | "microsoft" | "";

export interface CalendarConnection {
  provider: CalendarProvider;
  /** disconnected | active | broken. */
  status: string;
  /** The account that authorized — never a token. */
  account?: string;
  /**
   * Why the grant was last refused, as that provider said it. Carried
   * because the API carries it and deliberately not drawn: a broken
   * calendar has exactly one remedy, and "insufficient authentication
   * scopes" is the implementation explaining itself to somebody who
   * needs to press Connect.
   */
  last_error?: string;
  /** Which providers this deployment can offer, so the screen draws a
      button each rather than one for something nobody can connect. */
  offers: CalendarProvider[];
}

export const getCalendar = (
  signal?: AbortSignal,
): Promise<CalendarConnection> =>
  envelope("/api/admin/calendar", "calendar", { signal });

/**
 * Start connecting a calendar.
 *
 * Answers where the browser should go rather than connecting anything:
 * the grant is made at Google or Microsoft, and what comes back lands on
 * the public callback carrying the single-use state this call minted.
 */
export const authorizeCalendar = (
  provider: CalendarProvider,
): Promise<string> =>
  envelope("/api/admin/calendar/authorize", "authorize_url", {
    method: "POST",
    body: { provider },
  });

export const disconnectCalendar = (): Promise<CalendarConnection> =>
  envelope("/api/admin/calendar", "calendar", { method: "DELETE" });

// ─── Receptionist knowledge (receptionist_knowledge.go) ──────────────

/**
 * One switch, with the two facts about it kept apart.
 *
 * `enabled` is what the Company chose; `available` is whether its rental
 * system can answer the question at all. A switch the Backend cannot
 * serve arrives unavailable with a `reason`, and is shown that way rather
 * than offered and then failing mid-call. The server resolves the same
 * pair on the ringing-phone path, so a row reading unavailable here can
 * never be on during a call.
 */
export interface ReceptionistKnowledgeItem {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  available: boolean;
  /** Why it cannot be offered. Present only when `available` is false. */
  reason?: string;
  /** On the title, where the row is grey for want of a rental system. */
  tooltip?: string;
  /**
   * The switch this one hangs off, absent when it hangs off nothing. A
   * dependent row is rendered only while its parent is on (DESIGN.md),
   * and the server names the dependency so the screen does not have to
   * carry its own copy of which switch feeds which.
   */
  parent?: string;
}

/**
 * The switches, the rental system they read out of, and the one-line
 * read-back over them.
 *
 * `summary` is the server's own account of what the number does with a
 * caller's questions, built from the resolved switches — so it can never
 * claim an answer a broken connection has already withdrawn, which is
 * the whole reason it is not assembled here.
 */
export interface ReceptionistKnowledge {
  items: ReceptionistKnowledgeItem[];
  /** "Current RMS", "HireOS", or "your rental system" with none. */
  rental_system: string;
  summary: string;
}

interface ReceptionistKnowledgeBody {
  receptionist_knowledge: ReceptionistKnowledgeItem[] | null;
  rental_system?: string;
  summary?: string;
}

function knowledgeFrom(res: ReceptionistKnowledgeBody): ReceptionistKnowledge {
  return {
    items: res.receptionist_knowledge ?? [],
    rental_system: res.rental_system ?? "your rental system",
    summary: res.summary ?? "",
  };
}

export async function getReceptionistKnowledge(
  signal?: AbortSignal,
): Promise<ReceptionistKnowledge> {
  return knowledgeFrom(
    await apiFetch<ReceptionistKnowledgeBody>(
      "/api/admin/receptionist-knowledge",
      { signal },
    ),
  );
}

/** One switch per call — the server writes exactly the key it is sent. */
export async function saveReceptionistKnowledge(
  key: string,
  enabled: boolean,
): Promise<ReceptionistKnowledge> {
  return knowledgeFrom(
    await apiFetch<ReceptionistKnowledgeBody>(
      "/api/admin/receptionist-knowledge",
      { method: "PUT", body: { key, enabled } },
    ),
  );
}

// ─── Inquiry questions (inquiry_questions.go, ADR-0022) ─────────

export type InquiryQuestionType =
  | "text"
  | "textarea"
  | "integer"
  | "checkbox"
  | "select";

/**
 * One question a form asks, in the shape the public form reads
 * (`contentStep.packQuestions`) with the spoken form beside it: `ask` is
 * how the receptionist says it on the phone and `askHint` what it
 * listens for. Both blank means the label is said as written.
 *
 * The server also sends `generated` on a block it wrote from the
 * sentence the Company typed (#80); nothing here reads it, so it is not
 * typed.
 */
export interface InquiryQuestion {
  id: string;
  type: InquiryQuestionType;
  label: string;
  description?: string;
  hint?: string;
  required?: boolean;
  hiddenWhenTechRiderFile?: boolean;
  /**
   * The conditions it is asked under, all of which must hold: each an
   * answer to a yes-or-no or pick-one question above it on the list
   * (its id, and "yes"/"no" or one of its options), or to whether the
   * event is indoors or outdoors (`indoorOutdoor`). Absent is a question
   * always asked (ADR-0032).
   */
  when?: InquiryQuestionCondition[];
  options?: string[];
  /**
   * The answer a dry-hire enquiry is taken to have given. The generate
   * step sets it, on one question at most and never on one waiting on
   * another; the screen shows it on that question and offers no way to
   * change it.
   */
  dryHireAnswer?: string;
  ask?: string;
  askHint?: string;
}

/** One condition a question waits on. */
export interface InquiryQuestionCondition {
  field: string;
  equals: string;
}

/**
 * One line of the list as the screen sends it: the sentence, the id of
 * the block it was loaded from, and the conditions the Company put on
 * it. No id is a new question; an id with different words is a reword,
 * which keeps the id and has its block written again.
 */
export interface InquiryQuestionSentence {
  id?: string;
  label: string;
  when?: InquiryQuestionCondition[];
}

/**
 * One form's question set. The form is the Company's own, copied from a
 * standard template when it was set up (ADR-0027), so there is no
 * standard list behind it to tell apart.
 */
export interface InquiryQuestionForm {
  profile_key: string;
  label: string;
  questions: InquiryQuestion[];
  /** Whether a Customer may collect this form's gear (migration 052). */
  pick_up: boolean;
  /** Whether this form offers a site visit before the quote (migration 053). */
  site_visit: boolean;
}

/**
 * Every form's question set, and what every form asks before the
 * Company's own questions, as one sentence the screen shows above the
 * list.
 */
export interface InquiryQuestions {
  forms: InquiryQuestionForm[];
  always_asks: string;
  /** The Company drafts no quote (#273), so it has no range to show. */
  quoting_off: boolean;
}

export async function getInquiryQuestions(
  signal?: AbortSignal,
): Promise<InquiryQuestions> {
  const res = await apiFetch<{
    forms: InquiryQuestionForm[] | null;
    always_asks: string | null;
    quoting_off?: boolean | null;
  }>("/api/admin/inquiry-questions", { signal });
  return {
    forms: res.forms ?? [],
    always_asks: res.always_asks ?? "",
    quoting_off: res.quoting_off === true,
  };
}

/**
 * Renames one of the Company's forms. The label is the only word for a
 * form anywhere a person looks; `profile_key` is the key of the template
 * the form was set up from and is never shown.
 */
export const saveInquiryProfileLabel = (
  profile_key: string,
  label: string,
): Promise<string> =>
  envelope(
    `/api/admin/inquiry-profiles/${encodeURIComponent(profile_key)}`,
    "label",
    { method: "PUT", body: { label } },
  );

/**
 * Says whether a Customer may collect one form's gear and run it
 * themselves. Off, a call never asks and nothing drafts as a pick-up.
 */
export const saveInquiryPickUp = (
  profile_key: string,
  pick_up: boolean,
): Promise<boolean> =>
  envelope(
    `/api/admin/inquiry-profiles/${encodeURIComponent(profile_key)}/pick-up`,
    "pick_up",
    { method: "PUT", body: { pick_up } },
  );

/**
 * Says whether one form offers a site visit before the quote. Off, the
 * form draws no checkbox and a call makes no offer.
 */
export const saveInquirySiteVisit = (
  profile_key: string,
  site_visit: boolean,
): Promise<boolean> =>
  envelope(
    `/api/admin/inquiry-profiles/${encodeURIComponent(profile_key)}/site-visit`,
    "site_visit",
    { method: "PUT", body: { site_visit } },
  );

/**
 * Writes one form's whole list as the sentences the Company typed, and
 * answers with the form now in force — the blocks the server kept for
 * the sentences that did not change, and the ones it wrote for the rest.
 *
 * The whole list rather than a question at a time: reordering is half of
 * what the screen does, a per-row call would make one move two writes
 * with a broken order in between, and every new sentence in one call is
 * what lets the server write them as one form. An empty array is a form
 * that asks only the core steps. (The server also takes `null` as a reset
 * to the template's list; nothing on this screen offers one.)
 */
export const saveInquiryQuestions = (
  profile_key: string,
  questions: InquiryQuestionSentence[],
): Promise<InquiryQuestionForm> =>
  envelope("/api/admin/inquiry-questions", "form", {
    method: "PUT",
    body: { profile_key, questions },
  });

// ─── Inquiry email (inquiry_email_settings.go) ─────────────────

/** The whole section: the copy, who it goes to, and whether it is sent. */
export interface InquiryEmail {
  opening_note: string;
  closing_note: string;
  /** Off stops the notification only — the job still drafts. */
  enabled: boolean;
  /** Beside the notification address in General, never instead of it. */
  extra_recipients: string[];
}

/** Partial update — only supplied keys are written. */
export type InquiryEmailUpdate = Partial<InquiryEmail>;

export const getInquiryEmail = (signal?: AbortSignal): Promise<InquiryEmail> =>
  envelope("/api/admin/inquiry-email", "inquiry_email", { signal });

export const saveInquiryEmail = (
  payload: InquiryEmailUpdate,
): Promise<InquiryEmail> =>
  envelope("/api/admin/inquiry-email", "inquiry_email", {
    method: "PUT",
    body: payload,
  });

// ─── People (company_users.go) ────────────────────────────────────────

export interface CompanyUser {
  id: string;
  email: string;
  /** The signed-in person's own row, which carries no Remove control. */
  you: boolean;
  /**
   * Whether this person has an authenticator app set up. Null when the
   * server could not ask, and absent on a row it has not listed yet.
   */
  two_factor?: boolean | null;
}

export interface CompanyUsers {
  users: CompanyUser[];
  /** The Company's switch: everyone signs in with an authenticator code. */
  twoFactorRequired: boolean;
}

export async function listCompanyUsers(
  signal?: AbortSignal,
): Promise<CompanyUsers> {
  const res = await apiFetch<{
    users: CompanyUser[];
    two_factor_required?: boolean;
  }>("/api/admin/users", { signal });
  return {
    users: res.users ?? [],
    twoFactorRequired: res.two_factor_required ?? false,
  };
}

/**
 * Turning it off removes every authenticator app at the Company first,
 * so nobody is asked for a code the switch says is not required.
 */
export const setTwoFactorRequired = (required: boolean): Promise<boolean> =>
  envelope("/api/admin/users/two-factor", "two_factor_required", {
    method: "PUT",
    body: { required },
  });

/** Removes one other person's authenticator app, for a lost phone. */
export async function resetCompanyUserTwoFactor(id: string): Promise<void> {
  await apiFetch<void>(
    `/api/admin/users/${encodeURIComponent(id)}/two-factor`,
    { method: "DELETE" },
  );
}

/**
 * The server emails the new person their set-password link; the caller
 * never sees it.
 */
export const inviteCompanyUser = (email: string): Promise<CompanyUser> =>
  envelope("/api/admin/users", "user", { method: "POST", body: { email } });

export async function removeCompanyUser(id: string): Promise<void> {
  await apiFetch<void>(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ─── Estimate (inquiry_profiles_admin.go) ─────────────────────────────

/** The money a Company puts on top of the gear the model chose. */
export interface EstimateConfig {
  enabled: boolean;
  /** Total width of the range around the midpoint; 0 reads as the default. */
  spreadPercent: number;
  /** A minimum midpoint — the Company's callout minimum. */
  floor: number;
}

export interface InquiryProfileEstimate {
  profile_key: string;
  label: string;
  route_slug: string;
  enabled: boolean;
  is_default: boolean;
  estimate_config: EstimateConfig;
}

export const listInquiryProfileEstimates = (
  signal?: AbortSignal,
): Promise<InquiryProfileEstimate[]> =>
  envelope("/api/admin/inquiry-profiles", "profiles", { signal }, []);

/**
 * Writes one form's whole estimate config. The whole object rather than
 * the field that changed: the server reads it as one config, and a patch
 * of one number would have to be merged against a copy that may already
 * be stale.
 */
export const saveEstimateConfig = (
  profile_key: string,
  config: EstimateConfig,
): Promise<EstimateConfig> =>
  envelope(
    `/api/admin/inquiry-profiles/${encodeURIComponent(profile_key)}/estimate-config`,
    "estimate_config",
    { method: "PUT", body: config },
  );
